import { getStartOfDay } from '@/storybook/components/FormCollection/SingleDateFilter/helpers';
import { Period } from '@/types/date';
import { TranslationKeys } from '@/types/translation';
import { DateFormat, toFormattedDateOrNull } from '@/utils/date/format';
import { formatPeriodShortWithYear } from '@/utils/date/timePeriods';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

export const MIN_SELECTABLE_DATE = (() => {
  const twoYearsAgo = new Date();
  twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
  return getStartOfDay(twoYearsAgo);
})();

export interface ValidationError {
  message?: TranslationKeys;
  params?: Record<string, string | number>;
}

interface ParsedDateRange {
  start: Dayjs | null;
  end: Dayjs | null;
}

const MONTH_NAMES: Record<string, string> = {
  january: 'Jan',
  february: 'Feb',
  march: 'Mar',
  april: 'Apr',
  may: 'May',
  june: 'Jun',
  july: 'Jul',
  august: 'Aug',
  september: 'Sep',
  october: 'Oct',
  november: 'Nov',
  december: 'Dec',
  jan: 'Jan',
  feb: 'Feb',
  mar: 'Mar',
  apr: 'Apr',
  jun: 'Jun',
  jul: 'Jul',
  aug: 'Aug',
  sep: 'Sep',
  oct: 'Oct',
  nov: 'Nov',
  dec: 'Dec',
};

const NUMERIC_MONTHS: Record<string, string> = {
  '01': 'Jan',
  '02': 'Feb',
  '03': 'Mar',
  '04': 'Apr',
  '05': 'May',
  '06': 'Jun',
  '07': 'Jul',
  '08': 'Aug',
  '09': 'Sep',
  '10': 'Oct',
  '11': 'Nov',
  '12': 'Dec',
};

const DATE_FORMATS = [
  DateFormat.dateFormat,
  'D MMM YYYY',
  'DD MMM YY',
  'D MMM YY',
  'DD MMMM YYYY',
  'D MMMM YYYY',
  'DD MM YYYY',
  'D MM YYYY',
];

export const normalizeDate = (input: string): string => {
  let normalized = input.trim();
  normalized = normalized.replace(/\s+/g, ' ');
  normalized = normalized.replace(/[/\-_.]/g, ' ');

  const sortedEntries = Object.entries(MONTH_NAMES).sort((a, b) => b[0].length - a[0].length);
  sortedEntries.forEach(([full, short]) => {
    normalized = normalized.replace(new RegExp(`\\b${full}\\b`, 'gi'), short);
  });

  const parts = normalized.split(' ');
  if (parts.length === 3) {
    const [day, middle, year] = parts;
    if (/^\d{1,2}$/.test(day) && /^\d{2}$/.test(middle) && /^\d{4}$/.test(year)) {
      const monthShort = NUMERIC_MONTHS[middle];
      if (monthShort) {
        normalized = `${day} ${monthShort} ${year}`;
      }
    }
  }

  return normalized;
};

export const parseDateRange = (input: string): ParsedDateRange => {
  const parts = input.split(' - ').map(datePart => datePart.trim());
  if (parts.length !== 2) {
    return { start: null, end: null };
  }

  let startDate: Dayjs | null = null;
  let endDate: Dayjs | null = null;

  const normalizedStart = normalizeDate(parts[0]);
  const normalizedEnd = normalizeDate(parts[1]);

  for (const format of DATE_FORMATS) {
    if (!startDate) {
      const parsed = dayjs(normalizedStart, format, true);
      if (parsed.isValid()) {
        startDate = parsed;
      }
    }
    if (!endDate) {
      const parsed = dayjs(normalizedEnd, format, true);
      if (parsed.isValid()) {
        endDate = parsed;
      }
    }
  }

  return { start: startDate, end: endDate };
};

export const validateDateRange = (start: Dayjs | null, end: Dayjs | null): ValidationError => {
  const today = dayjs();

  if (!start || !end) {
    return { message: 'components.dateRangeInput.validation.invalidFormat' };
  }

  if (start.isAfter(today, 'day') || end.isAfter(today, 'day')) {
    return { message: 'components.dateRangeInput.validation.futureDate' };
  }

  if (end.isBefore(start, 'day')) {
    return { message: 'components.dateRangeInput.validation.endBeforeStart' };
  }

  if (start.isBefore(MIN_SELECTABLE_DATE, 'day') || end.isBefore(MIN_SELECTABLE_DATE, 'day')) {
    return {
      message: 'components.dateRangeInput.validation.minDate',
      params: { minDate: toFormattedDateOrNull(MIN_SELECTABLE_DATE, DateFormat.dateFormat, '') },
    };
  }

  return {};
};

export const formatPeriodValue = (period: Period | undefined): string => {
  if (!period?.beginn || !period?.end) {
    return '';
  }
  return formatPeriodShortWithYear(period);
};

export const formatDateRangeString = (start: Dayjs, end: Dayjs): string => {
  const formattedStart = toFormattedDateOrNull(start, DateFormat.dateFormat);
  const formattedEnd = toFormattedDateOrNull(end, DateFormat.dateFormat);

  if (!formattedStart || !formattedEnd) {
    return '';
  }

  return `${formattedStart} - ${formattedEnd}`;
};

export const formatPartialDateRange = (startDate: Date | undefined, endDate: Date | undefined): string => {
  const formattedStart = startDate ? toFormattedDateOrNull(startDate, DateFormat.dateFormat) : null;

  if (!formattedStart) {
    return '';
  }

  const formattedEnd = endDate ? toFormattedDateOrNull(endDate, DateFormat.dateFormat, '') : '';

  return `${formattedStart} - ${formattedEnd}`;
};

export { autoFormatDateRangeInput } from './autoFormatDateRangeInput';
export { isCompleteDateRange, validateInputInProgress } from './dateRangeInputValidation';
